import { useEffect, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, Building2, MapPin, Download, TrendingUp, TrendingDown } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";
import { hoursToHM } from "@/lib/duration";

const hrs = (v) => (v ? hoursToHM(v) : "—");

const exportCsv = (data, start, end) => {
  const rows = [["Type", "Site / Property", "Client", "Cleaner", "Date", "Hours", "Breaks (not counted)"]];
  const push = (type, g) => g.workers.forEach((w) => w.days.forEach((d) => rows.push([type, g.name, g.clientName || "", w.name, d.date, d.hours, d.breaks || 0])));
  data.sites.forEach((g) => push("Site", g));
  data.properties.forEach((g) => push("Field job", g));
  const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `hours-by-site_${start}_${end}.csv`;
  a.click();
};

const Trend = ({ t, weekStarts }) => {
  if (!t) return null;
  const data = t.weeks.map((h, i) => ({ h, w: weekStarts[i]?.slice(5) }));
  const up = t.changePct != null && t.changePct >= 15;
  const down = t.changePct != null && t.changePct <= -15;
  return (
    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()} data-testid={`trend-${t.id}`}>
      <div className="h-7 w-28">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <Tooltip formatter={(v) => hrs(v)} labelFormatter={(_, p) => `Week of ${p?.[0]?.payload?.w || ""}`} contentStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="h" stroke={up ? "#d97706" : "#0d9488"} strokeWidth={2} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {t.changePct != null && (
        <span className={`text-[11px] font-semibold flex items-center ${up ? "text-amber-600" : down ? "text-sky-600" : "text-slate-400"}`} title={`This week vs ${t.weeks.length - 1}-week average (${hrs(t.avgPrev)})`}>
          {t.changePct >= 0 ? <TrendingUp className="h-3 w-3 mr-0.5" /> : <TrendingDown className="h-3 w-3 mr-0.5" />}{t.changePct > 0 ? "+" : ""}{t.changePct}%
        </span>
      )}
    </div>
  );
};

const Group = ({ g, tone, testid, q, trend, weekStarts }) => {
  const [open, setOpen] = useState(false);
  const workers = g.workers.filter((w) => !q || w.name.toLowerCase().includes(q));
  if (!workers.length) return null;
  return (
    <Card className="overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 cursor-pointer" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <span className="font-semibold text-slate-900 truncate">{g.name}</span>
          {g.clientName && <span className="text-xs text-slate-500">{g.clientName}</span>}
          <Badge variant="outline" className="bg-white text-slate-600">{workers.length} cleaner{workers.length === 1 ? "" : "s"}</Badge>
          {g.breaks > 0 && <Badge variant="outline" className="bg-slate-100 text-slate-500">Breaks {hrs(g.breaks)} (not counted)</Badge>}
        </div>
        <div className="flex items-center gap-3">
          <Trend t={trend} weekStarts={weekStarts} />
          <Badge className={`${tone} text-white font-semibold`} data-testid={`${testid}-total`}>{hrs(g.hours)}</Badge>
        </div>
      </div>
      {open && (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase text-slate-400"><th className="px-4 py-2">Cleaner</th><th className="px-3 py-2">Days</th><th className="px-3 py-2 text-right">Hours</th></tr></thead>
          <tbody>
            {workers.map((w) => (
              <tr key={w.workerId} className="border-t border-slate-50 align-top">
                <td className="px-4 py-2 font-medium text-slate-800">{w.name}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{w.days.map((d) => `${d.date.slice(5)} ${hrs(d.hours)}`).join(" · ")}</td>
                <td className="px-3 py-2 text-right font-semibold text-slate-800">{hrs(w.hours)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
};

export const HoursBySite = ({ start, end, q }) => {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  useEffect(() => {
    client.get(`/timesheets/site-trend?weeks=8&end=${end}`).then((r) => setTrend(r.data)).catch(() => setTrend(null));
  }, [end]);
  useEffect(() => {
    setData(null);
    client.get(`/timesheets/by-site?start=${start}&end=${end}`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  }, [start, end]);
  if (!data) return <p className="text-slate-400">Loading…</p>;
  const empty = !data.sites.length && !data.properties.length;
  return (
    <div className="space-y-6" data-testid="hours-by-site">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200"><Building2 className="h-3 w-3 mr-1" />Sites {hoursToHM(data.totalSite || 0)}</Badge>
        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200"><MapPin className="h-3 w-3 mr-1" />Field jobs {hoursToHM(data.totalField || 0)}</Badge>
        <Button size="sm" variant="outline" className="ml-auto" disabled={empty} onClick={() => exportCsv(data, start, end)} data-testid="hours-by-site-export"><Download className="h-4 w-4 mr-1.5" />Export CSV</Button>
      </div>
      {empty ? <Card className="p-8 text-center text-slate-400" data-testid="hours-by-site-empty">No hours recorded for this period.</Card> : (
        <>
          {data.sites.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-teal-700 flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />Sites</h3>
              {data.sites.map((g) => <Group key={g.id || "none"} g={g} q={q} tone="bg-teal-600 hover:bg-teal-600" testid={`hbs-site-${g.id || "none"}`} trend={trend?.sites.find((x) => x.id === g.id)} weekStarts={trend?.weekStarts || []} />)}
            </section>
          )}
          {data.properties.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-orange-700 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />Field jobs</h3>
              {data.properties.map((g) => <Group key={g.id || "none"} g={g} q={q} tone="bg-orange-600 hover:bg-orange-600" testid={`hbs-prop-${g.id || "none"}`} />)}
            </section>
          )}
        </>
      )}
    </div>
  );
};
